import { useEffect, useRef, useState } from "react";

import { NovedadesExportService } from "@services/NovedadesExportService";
import { NovedadesExportServiceModel } from "@services/NovedadesExportService";
import { UsersService } from "@services/UsersService";
import { CheckButton, DateTriggerButton, HollowedButton, NormalButton } from "@shared/designSystem/Button";
import { ComboBox, ComboBoxOption } from "@shared/designSystem/ComboBox";
import {
    HorizontalContainer,
    TableCell,
    TableHeaderCell,
    TableHeaderContainer,
    TableRowContainer,
    VerticalContainer
} from "@shared/designSystem/Containers";
import { RadioButton } from "@shared/designSystem/RadioButton";
import SearchBar from "@shared/designSystem/SearchBar";
import { TextInput, TextInputRef } from "@shared/designSystem/TextInput";
import { SmallText, Text } from "@shared/designSystem/Texts";
import { ToastManager } from "@shared/designSystem/messages/ToastManager";
import designSystemStyles from "@shared/styles/designSystemStyles";

import {
    ExportNovedadesEmployeeListItem,
    useExportNovedadesCreateScreenViewModel,
} from "./ExportNovedadesCreateScreenViewModel";
import LocalDateSelector from "../Notificaciones/LocalDateSelector";

interface ExportNovedadesCreateScreenProps {
    isOpen: boolean;
    orgID: string;
    service: NovedadesExportService;
    usersService: UsersService;
    onClose: () => void;
    onCreated?: (process: NovedadesExportServiceModel.ProcessRecord) => Promise<void> | void;
}

const ExportNovedadesCreateScreen: React.FC<ExportNovedadesCreateScreenProps> = ({
    isOpen,
    orgID,
    service,
    usersService,
    onClose,
    onCreated,
}) => {
    const viewModel = useExportNovedadesCreateScreenViewModel({
        isOpen,
        orgID,
        service,
        usersService,
    });
    const titleInputRef = useRef<TextInputRef>(null);
    const [dateSelectorField, setDateSelectorField] = useState<"from" | "to" | null>(null);
    const profileOptions: ComboBoxOption[] = viewModel.exportProfiles.map((profile) => ({
        id: profile.profileID,
        title: profile.label || profile.profileID,
    }));
    const selectedProfileOption = profileOptions.find((option) => option.id === viewModel.calculationProfileID);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const timeout = window.setTimeout(() => {
            titleInputRef.current?.focus();
        }, 0);

        return () => window.clearTimeout(timeout);
    }, [isOpen]);

    const handleSubmit = async () => {
        const result = await viewModel.submit();

        if (!result) {
            return;
        }

        if (result.kind === "success") {
            ToastManager.shared().showSuccessMessage(result.message ?? "Exportación creada correctamente.".localized());
            if (result.process) {
                void onCreated?.(result.process);
            }
            return;
        }

        ToastManager.shared().showErrorMessage(result.errorMessage ?? "No se pudo crear la exportación.".localized());
    };

    const handleDateSelected = (date: Date | null) => {
        if (dateSelectorField === "from") {
            viewModel.setDateFrom(date);
        }
        if (dateSelectorField === "to") {
            viewModel.setDateTo(date);
        }
        setDateSelectorField(null);
    };

    const renderDateSelector = () => {
        if (dateSelectorField == null) {
            return null;
        }

        return (
            <div
                style={{
                    position: "absolute",
                    top: "100%",
                    left: dateSelectorField === "from" ? 0 : "232px",
                    zIndex: 20,
                    marginTop: designSystemStyles.gapXxxSm,
                    width: "420px",
                    maxWidth: "calc(100vw - 48px)",
                    padding: designSystemStyles.paddingSm,
                    backgroundColor: "white",
                    borderRadius: designSystemStyles.cornerRadiusComponent,
                    boxShadow: "0 12px 32px rgba(0, 0, 0, 0.16)",
                }}
            >
                <VerticalContainer width="100%" gap={designSystemStyles.gapSm}>
                    <LocalDateSelector
                        initialDate={dateSelectorField === "from" ? viewModel.dateFrom : viewModel.dateTo}
                        disableFuture={false}
                        events={{
                            onApply: handleDateSelected,
                            onClear: () => handleDateSelected(null),
                        }}
                    />
                </VerticalContainer>
            </div>
        );
    };

    const renderEmployeeRow = (employee: ExportNovedadesEmployeeListItem) => {
        const selected = viewModel.isEmployeeSelected(employee.id);

        return (
            <div
                key={employee.id}
                role="button"
                tabIndex={0}
                onClick={() => viewModel.toggleEmployee(employee.id)}
                onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        viewModel.toggleEmployee(employee.id);
                    }
                }}
                style={{ width: "100%", cursor: "pointer" }}
            >
                <TableRowContainer>
                    <HorizontalContainer width="36px" justifyContent="center">
                        <CheckButton
                            text=""
                            selected={selected}
                            onClick={() => {}}
                        />
                    </HorizontalContainer>
                    <HorizontalContainer width="50%" minWidth="0" overflow="hidden">
                        <Text
                            text={`**${(employee.name ?? '').trim()}**`}
                            markdown={true}
                            truncate={true}
                        />
                    </HorizontalContainer>
                    <TableCell text={employee.legajo} width="22%" truncate={true} />
                    <TableCell text={employee.phoneNumber} width="28%" truncate={true} />
                </TableRowContainer>
            </div>
        );
    };

    return (
        <VerticalContainer height="100%" gap={designSystemStyles.gapMd}>
            <div style={{ display: "flex", flexDirection: "column", gap: designSystemStyles.gapSm, width: "100%", height: "100%", minHeight: 0 }}>
                <TextInput
                    ref={titleInputRef}
                    label={"Título".localized()}
                    value={viewModel.title}
                    onChange={viewModel.setTitle}
                    placeholder={"Quincena mayo 2026".localized()}
                />
                {profileOptions.length === 0 && viewModel.exportProfilesLoading && (
                    <Text
                        text={"Cargando perfiles de exportación...".localized()}
                        color={designSystemStyles.colorTextSecondary}
                    />
                )}
                {viewModel.exportProfilesError && (
                    <Text text={viewModel.exportProfilesError} color={designSystemStyles.colorError} />
                )}
                <div style={{ position: "relative", width: "100%" }}>
                    <HorizontalContainer
                        gap={designSystemStyles.gapSm}
                        alignment="start"
                        justifyContent="flex-start"
                        width="auto"
                    >
                        <VerticalContainer gap={designSystemStyles.gapXxxSm} width="220px" margin="0">
                            <SmallText text={"Desde".localized()} />
                            <DateTriggerButton
                                text={viewModel.dateFromValue}
                                onClick={() => setDateSelectorField("from")}
                                width="220px"
                            />
                        </VerticalContainer>
                        <VerticalContainer gap={designSystemStyles.gapXxxSm} width="220px" margin="0">
                            <SmallText text={"Hasta".localized()} />
                            <DateTriggerButton
                                text={viewModel.dateToValue}
                                onClick={() => setDateSelectorField("to")}
                                width="220px"
                            />
                        </VerticalContainer>
                    </HorizontalContainer>
                    {renderDateSelector()}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: designSystemStyles.gapSm, width: "100%", minHeight: 0 }}>
                    <SmallText text={"Colaboradores".localized()} />
                    <div role="radiogroup">
                        <HorizontalContainer gap={designSystemStyles.gapLg}>
                            <RadioButton
                                text={"Todos los colaboradores".localized()}
                                selected={!viewModel.selectedEmployeeIDsRequired}
                                onClick={viewModel.setAllEmployeesMode}
                            />
                            <RadioButton
                                text={"Colaboradores específicos".localized()}
                                selected={viewModel.selectedEmployeeIDsRequired}
                                onClick={viewModel.setSpecificEmployeesMode}
                            />
                        </HorizontalContainer>
                    </div>
                    {viewModel.selectedEmployeeIDsRequired && (
                        <div style={{ display: "flex", flexDirection: "column", width: "100%", height: "100%", minHeight: 0, gap: designSystemStyles.gapSm }}>
                            <HorizontalContainer width="100%">
                                <SearchBar
                                    placeholder={"Buscar colaborador".localized()}
                                    value={viewModel.employeeSearchField}
                                    onChange={viewModel.setEmployeeSearchField}
                                    width="460px"
                                />
                                <Text
                                    text={`${viewModel.selectedEmployeeIDs.length} ${"seleccionados".localized()}`}
                                    color={designSystemStyles.colorTextSecondary}
                                />
                            </HorizontalContainer>
                            <TableHeaderContainer height={designSystemStyles.sizeRowMedium}>
                                <TableHeaderCell text="" width="36px" justifyContent="center" />
                                <TableHeaderCell text={"Nombre".localized()} width="50%" truncate={true} />
                                <TableHeaderCell text={"Legajo".localized()} width="22%" truncate={true} />
                                <TableHeaderCell text={"Teléfono".localized()} width="28%" truncate={true} />
                            </TableHeaderContainer>
                            <div style={{ display: "flex", flexDirection: "column", gap: designSystemStyles.gapNone, overflowY: "auto", flex: 1, minHeight: 0, width: "100%" }}>
                                {viewModel.employeesLoading ? (
                                    <HorizontalContainer width="100%" justifyContent="center" padding={designSystemStyles.paddingSm}>
                                        <Text text={"Cargando colaboradores...".localized()} />
                                    </HorizontalContainer>
                                ) : viewModel.employeesError ? (
                                    <HorizontalContainer width="100%" justifyContent="center" padding={designSystemStyles.paddingSm}>
                                        <Text text={viewModel.employeesError} color={designSystemStyles.colorError} />
                                    </HorizontalContainer>
                                ) : viewModel.filteredEmployees.length === 0 ? (
                                    <HorizontalContainer width="100%" justifyContent="center" padding={designSystemStyles.paddingSm}>
                                        <Text
                                            text={viewModel.employees.length === 0
                                                ? "No hay colaboradores para mostrar.".localized()
                                                : "No hay colaboradores que coincidan con la búsqueda.".localized()}
                                        />
                                    </HorizontalContainer>
                                ) : (
                                    viewModel.filteredEmployees.map(renderEmployeeRow)
                                )}
                            </div>
                        </div>
                    )}
                </div>
                {!viewModel.hasValidDateRange && (viewModel.dateFrom || viewModel.dateTo) && (
                    <Text
                        text={"La fecha desde debe ser menor o igual a la fecha hasta.".localized()}
                        color={designSystemStyles.colorError}
                    />
                )}
                {viewModel.latestSubmissionResult?.kind === "error" && viewModel.latestSubmissionResult.errorMessage && (
                    <VerticalContainer
                        gap={designSystemStyles.gapXxxSm}
                        padding={designSystemStyles.paddingSm}
                        backgroundColor="#FFF3F1"
                        borderRadius={designSystemStyles.cornerRadiusComponent}
                    >
                        <SmallText text={"Último error".localized()} />
                        <Text text={viewModel.latestSubmissionResult.errorMessage} />
                    </VerticalContainer>
                )}
            </div>

            <HorizontalContainer justifyContent="flex-end" gap={designSystemStyles.gapSm}>
                <HollowedButton
                    text={"Crear exportación".localized()}
                    onClick={() => void handleSubmit()}
                    disabled={!viewModel.canSubmit}
                    loading={viewModel.isSubmitting}
                    width="220px"
                />
                <HollowedButton
                    text={"Cerrar".localized()}
                    onClick={onClose}
                    width="160px"
                />
            </HorizontalContainer>
        </VerticalContainer>
    );
};

export default ExportNovedadesCreateScreen;
